import { useState } from "react";
import { FileText, Loader2, CheckCircle, Send, Printer } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export default function Reporting() {
  const { orgId, familyId } = useTenant();
  const queryClient = useQueryClient();
  const [generating, setGenerating] = useState(false);
  const [reportType, setReportType] = useState("quarterly");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [currentReport, setCurrentReport] = useState<{
    report_id: string;
    narrative: string;
    summary: Record<string, number>;
  } | null>(null);

  // Fetch existing reports
  const { data: reports, isLoading } = useQuery({
    queryKey: ["client-reports", orgId, familyId],
    queryFn: async () => {
      if (!orgId) return [];

      let query = supabase
        .from("client_reports")
        .select("*")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false });

      if (familyId) query = query.eq("family_id", familyId);

      const { data, error } = await query.limit(20);
      if (error) throw error;
      return data || [];
    },
    enabled: !!orgId,
  });

  const generateReport = async () => {
    if (!periodStart || !periodEnd) {
      toast.error("Please select a reporting period");
      return;
    }

    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-client-report", {
        body: { orgId, familyId, reportType, periodStart, periodEnd },
      });
      if (error) throw error;
      setCurrentReport(data);
      toast.success("Report generated successfully");
      queryClient.invalidateQueries({ queryKey: ["client-reports"] });
    } catch (err) {
      toast.error("Failed to generate report: " + (err instanceof Error ? err.message : "Unknown error"));
    } finally {
      setGenerating(false);
    }
  };

  const approveReport = async (reportId: string) => {
    try {
      const { error } = await supabase
        .from("client_reports")
        .update({
          status: "approved",
          approved_by: (await supabase.auth.getUser()).data.user?.id,
          approved_at: new Date().toISOString(),
        })
        .eq("id", reportId);

      if (error) throw error;
      toast.success("Report approved");
      queryClient.invalidateQueries({ queryKey: ["client-reports"] });
    } catch (err) {
      toast.error("Failed to approve report");
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "draft": return <Badge variant="secondary">Draft</Badge>;
      case "review": return <Badge variant="default">In Review</Badge>;
      case "approved": return <Badge variant="default"><CheckCircle className="mr-1 h-3 w-3" />Approved</Badge>;
      case "sent": return <Badge variant="outline"><Send className="mr-1 h-3 w-3" />Sent</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoading) return <div className="flex-1 p-8">Loading...</div>;

  return (
    <div className="flex-1 space-y-6 px-4 py-6">

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Client Report Generator</h1>
          <p className="text-muted-foreground">Generate AI-powered quarterly and annual client reports</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Generate New Report</CardTitle>
          <CardDescription>Select a period and report type to generate a client-facing report</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Report Type</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                  <SelectItem value="custom">Custom Period</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Period Start</Label>
              <Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Period End</Label>
              <Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
            </div>
          </div>
          <Button onClick={generateReport} disabled={generating}>
            {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
            {generating ? "Generating..." : "Generate Report"}
          </Button>
        </CardContent>
      </Card>

      {currentReport && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Report Preview</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => window.print()}>
                  <Printer className="mr-2 h-4 w-4" />Print
                </Button>
                <Button size="sm" onClick={() => approveReport(currentReport.report_id)}>
                  <CheckCircle className="mr-2 h-4 w-4" />Approve
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {currentReport.summary && (
              <div className="grid grid-cols-3 gap-4">
                <div className="rounded-lg border p-3">
                  <div className="text-sm text-muted-foreground">Total NAV</div>
                  <div className="text-lg font-bold font-mono">${(currentReport.summary.totalNav || 0).toLocaleString()}</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-sm text-muted-foreground">Net Cash Flow</div>
                  <div className="text-lg font-bold font-mono">${(currentReport.summary.netCashFlow || 0).toLocaleString()}</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-sm text-muted-foreground">Total Fees</div>
                  <div className="text-lg font-bold font-mono">${(currentReport.summary.totalFees || 0).toLocaleString()}</div>
                </div>
              </div>
            )}
            <Separator />
            <div className="prose prose-sm max-w-none">
              {currentReport.narrative.split("\n").map((paragraph, i) => (
                <p key={i} className="mb-3">{paragraph}</p>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Previous Reports</CardTitle>
          <CardDescription>History of generated reports</CardDescription>
        </CardHeader>
        <CardContent>
          {reports && reports.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Generated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell className="capitalize font-medium">{report.report_type}</TableCell>
                    <TableCell>{report.period_start} to {report.period_end}</TableCell>
                    <TableCell>{getStatusBadge(report.status)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(report.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {report.status === "draft" && (
                        <Button variant="outline" size="sm" onClick={() => approveReport(report.id)}>
                          Approve
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">No reports generated yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
